<template>
  <div class="modal-overlay" @click.self="$emit('close')" role="dialog" aria-modal="true" aria-labelledby="subscription-modal-heading">
    <div class="modal-content" :class="{ 'modal-content-compact': !showPlans, 'modal-content-plans': showPlans }">
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h10v2H4v-2z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 id="subscription-modal-heading">{{ isSubUser ? 'Subscription access' : 'Manage subscription' }}</h2>
            <p>{{ isSubUser ? 'Account requires admin action' : 'Your current plan and billing details' }}</p>
          </div>
        </div>
        <button type="button" class="close-btn" @click="$emit('close')" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="body-wrap">
        <div v-if="loading" class="state-block" role="status" aria-live="polite">
          <div class="loader-card">
            <div class="spinner" aria-hidden="true" />
            <p>Loading subscription details...</p>
            <small>Please wait while we fetch your latest plan status.</small>
          </div>
        </div>

        <template v-else-if="isSubUser">
          <div class="subuser-message-card" role="status" aria-live="polite">
            <div class="subuser-message-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 9v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Plan expired. Contact admin to reactivate this account.</h3>
            <p>
              Subuser accounts cannot purchase plans directly. Please ask your primary admin to renew or upgrade the subscription.
            </p>
            <button type="button" class="btn-secondary" @click="$emit('close')">Close</button>
          </div>
        </template>

        <template v-else-if="showPlans">
          <div class="plans-header">
            <p class="plans-subtitle">
              Choose this plan to unlock all dashboard modules. Every plan includes full feature access; only property capacity changes.
            </p>
          </div>

          <div v-if="plans.length" class="plans-grid" :class="{ 'plans-grid-single': plans.length === 1 }">
            <article
              v-for="plan in plans"
              :key="plan.planId"
              class="plan-card"
              :class="{ 'is-highlighted': normalized?.planId && normalized.planId === plan.planId }"
            >
              <div class="plan-card-top">
                <div>
                  <h4 class="plan-card-name">{{ plan.planname || 'Plan' }}</h4>
                  <p class="plan-card-desc">{{ plan.description || 'Subscription plan' }}</p>
                </div>
                <span class="plan-card-status">{{ formatStatus(plan.planstatus || 'ACTIVE') }}</span>
              </div>

              <div class="plan-card-price-row">
                <span class="plan-card-price">{{ formatMoney(plan.amount, plan.currency) }}</span>
                <span class="plan-card-interval">per property / {{ mapInterval(plan.planinterval).replace('per ', '') }}</span>
              </div>

              <div class="plan-card-meta">
                <span class="meta-chip">Pay only for the properties you subscribe</span>
                <span class="meta-chip">Billed monthly</span>
              </div>

              <div class="plan-feature-wrap">
                <ul class="feature-pill-list" aria-label="Included features">
                  <li v-for="feature in includedFeatures" :key="`${plan.planId}-${feature}`" class="feature-pill">{{ feature }}</li>
                </ul>
              </div>

              <button
                type="button"
                class="btn-primary subscribe-btn"
                @click="subscribeToPlan(plan)"
              >
                Subscribe
              </button>
            </article>
          </div>

          <div v-else class="state-block state-error">
            <p>No plans are available at the moment.</p>
          </div>
        </template>

        <div v-else-if="errorMessage" class="state-block state-error">
          <p>{{ errorMessage }}</p>
          <button type="button" class="btn-secondary" @click="fetchSubscription">Try again</button>
        </div>

        <template v-else-if="normalized">
          <div class="subscribed-layout">
            <div class="summary-strip">
              <div class="summary-strip-text">
                <div class="summary-title-line">
                  <h3 class="summary-plan-name">{{ normalized.planName || '—' }}</h3>
                  <span class="status-pill" :class="statusClass">{{ formatStatus(normalized.planStatus) }}</span>
                </div>
                <p v-if="normalized.description" class="summary-plan-desc">{{ normalized.description }}</p>
              </div>
              <div class="summary-strip-price">
                <span class="summary-price">{{ formatMoney(normalized.amount, normalized.currency) }}</span>
                <span v-if="intervalLabel" class="summary-interval">{{ intervalLabel }}</span>
              </div>
            </div>

            <div class="stat-chip-grid" aria-label="Subscription details">
              <div v-if="normalized.planId" class="stat-chip">
                <span class="stat-chip-label">Plan ID</span>
                <span class="stat-chip-value mono">{{ normalized.planId }}</span>
              </div>
              <div v-if="normalized.objectCapacity != null" class="stat-chip">
                <span class="stat-chip-label">Capacity</span>
                <span class="stat-chip-value">{{ normalized.objectCapacity }} {{ normalized.objectCapacity === 1 ? 'property' : 'properties' }}</span>
              </div>
              <div v-if="normalized.purchasedSlots != null && normalized.purchasedSlots !== ''" class="stat-chip">
                <span class="stat-chip-label">Purchased slots</span>
                <span class="stat-chip-value">{{ normalized.purchasedSlots }}</span>
              </div>
              <div v-if="normalized.remainingSlots != null && normalized.remainingSlots !== ''" class="stat-chip stat-chip-highlight">
                <span class="stat-chip-label">Remaining slots</span>
                <span class="stat-chip-value">{{ normalized.remainingSlots }}</span>
              </div>
              <div v-if="normalized.trialPeriodDays > 0" class="stat-chip">
                <span class="stat-chip-label">Trial</span>
                <span class="stat-chip-value">{{ normalized.trialPeriodDays }} days</span>
              </div>
              <div v-if="normalized.planStartDate" class="stat-chip">
                <span class="stat-chip-label">Started</span>
                <span class="stat-chip-value">{{ formatDateShort(normalized.planStartDate) }}</span>
              </div>
              <div v-if="normalized.nextBilling" class="stat-chip">
                <span class="stat-chip-label">Renews / ends</span>
                <span class="stat-chip-value">{{ formatDateShort(normalized.nextBilling) }}</span>
              </div>
            </div>

            <section
              v-if="canShowDowngradeSection"
              class="downgrade-panel"
              aria-labelledby="downgrade-panel-title"
            >
              <div class="downgrade-panel-head">
                <h4 id="downgrade-panel-title" class="downgrade-panel-title">Slot downgrade</h4>
                <span class="downgrade-panel-meta">{{ purchasedSlotsCurrent }} purchased</span>
              </div>
              <p class="downgrade-panel-hint">
                Adjust your purchased slots for next billing cycle.
              </p>
              <div class="downgrade-panel-actions">
                <button
                  type="button"
                  class="btn-primary"
                  :disabled="isOpeningGateway || isDowngrading"
                  @click="promptSlotCountPurchase"
                >
                  Purchase Slot
                </button>
                <button
                  type="button"
                  class="btn-secondary downgrade-toggle-btn"
                  :disabled="isDowngrading"
                  @click="toggleDowngradeEditor"
                >
                  {{ showDowngradeEditor ? 'Cancel' : 'Downgrade slots' }}
                </button>
              </div>
              <div v-if="showDowngradeEditor" class="downgrade-panel-form">
                <label class="downgrade-input-wrap">
                  <span class="downgrade-input-label">Target purchased slots</span>
                  <input
                    v-model.number="targetPurchasedSlots"
                    class="downgrade-input"
                    type="number"
                    step="1"
                    placeholder="Enter slot value"
                    :disabled="isDowngrading"
                  />
                </label>
                <button
                  type="button"
                  class="btn-primary downgrade-submit-btn"
                  :disabled="!canSubmitDowngrade"
                  @click="openDowngradeConfirm"
                >
                  {{ isDowngrading ? 'Downgrading...' : 'Review downgrade' }}
                </button>
              </div>
            </section>

            <section class="properties-panel" aria-labelledby="properties-panel-heading">
              <div class="properties-panel-head">
                <h4 id="properties-panel-heading" class="properties-panel-title">Subscribed properties</h4>
                <span class="properties-count-badge" aria-label="Number of properties">{{ subscriptionObjectRows.length }}</span>
              </div>
              <p class="properties-panel-hint">Each property is billed on this plan. Scroll the list if you have many.</p>

              <p v-if="!subscriptionObjectRows.length" class="properties-empty" role="status">
                No properties linked yet—normal right after subscribing, before properties are attached.
              </p>

              <div v-else class="properties-table-scroller">
                <table class="properties-table">
                  <thead>
                    <tr>
                      <th scope="col" class="col-property">Property</th>
                      <th scope="col" class="col-status">Status</th>
                      <th scope="col" class="col-since">Since</th>
                      <th scope="col" class="col-action"><span class="vh">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in subscriptionObjectRows"
                      :key="`${row.objectId || 'row'}-${idx}`"
                      :class="subscriptionObjectRowClass(row)"
                    >
                      <td class="col-property">
                        <span class="cell-name">{{ displayPropertyLabel(row) }}</span>
                        <span v-if="displayPropertySecondary(row)" class="cell-id mono">{{ displayPropertySecondary(row) }}</span>
                        <span v-else-if="row.objectId" class="cell-id mono">{{ row.objectId }}</span>
                      </td>
                      <td class="col-status">
                        <span
                          class="sub-object-status"
                          :class="'sub-status-' + String(row.status || 'unknown').toLowerCase()"
                        >{{ formatStatus(row.status) }}</span>
                      </td>
                      <td class="col-since">
                        <span class="cell-date">{{ formatDateShort(row.subscribedAt) }}</span>
                        <span v-if="row.removedAt" class="cell-removed">Off {{ formatDateShort(row.removedAt) }}</span>
                      </td>
                      <td class="col-action">
                        <button
                          v-if="canRemoveSubscriptionObject(row)"
                          type="button"
                          class="btn-remove-property"
                          :disabled="removingObjectId === String(row.objectId)"
                          @click="openRemoveConfirm(row)"
                        >
                          {{ removingObjectId === String(row.objectId) ? 'Removing...' : 'Remove' }}
                        </button>
                        <span v-else-if="row.removedAt" class="cell-dash">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <p v-if="propertyActionMessage" class="property-action-hint" role="status">{{ propertyActionMessage }}</p>

            <div class="footer-actions footer-actions--subscribed">
              <button type="button" class="btn-secondary" @click="$emit('close')">Close</button>
              <button type="button" class="btn-primary" @click="fetchSubscription">Refresh</button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div v-if="showGatewayPicker" class="gateway-overlay" @click.self="closeGatewayPicker">
      <div class="gateway-card" role="dialog" aria-modal="true" aria-labelledby="gateway-title">
        <h3 id="gateway-title">Choose payment gateway</h3>
        <div v-if="gatewayLoading" class="loader-card loader-card-compact gateway-state">
          <div class="spinner spinner-sm" aria-hidden="true" />
          <p>Loading available gateways...</p>
        </div>
        <div v-else-if="isOpeningGateway" class="loader-card loader-card-compact gateway-state">
          <div class="spinner spinner-sm" aria-hidden="true" />
          <p>Opening payment gateway securely...</p>
        </div>
        <p v-else-if="gatewayError" class="gateway-state gateway-error">{{ gatewayError }}</p>
        <ul v-else class="gateway-list">
          <li v-for="gateway in gateways" :key="gateway.idgatewaycfg">
            <button
              type="button"
              class="gateway-btn"
              :disabled="isOpeningGateway"
              @click="selectGateway(gateway)"
            >
              <span class="gateway-btn-main">
                <span class="gateway-logo-wrap">
                  <img
                    v-if="gatewayLogo(gateway.idgatewaycfg)"
                    :src="gatewayLogo(gateway.idgatewaycfg)"
                    :alt="`${gateway.idgatewaycfg} logo`"
                    class="gateway-logo"
                  />
                </span>
                <span class="gateway-meta">
                  <strong>{{ gateway.idgatewaycfg }}</strong>
                  <small>{{ gateway.idgatewaycfg === 'ESEWA' ? 'Available now' : 'Coming soon' }}</small>
                </span>
              </span>
              <span class="gateway-chevron">›</span>
            </button>
          </li>
        </ul>
        <div class="gateway-actions">
          <button type="button" class="btn-secondary" :disabled="isOpeningGateway" @click="closeGatewayPicker">Cancel</button>
        </div>
      </div>
    </div>

    <div v-if="showPropertyCountPrompt" class="gateway-overlay" @click.self="closePropertyCountPrompt">
      <div class="gateway-card" role="dialog" aria-modal="true" aria-labelledby="property-count-title">
        <h3 id="property-count-title">How many properties?</h3>
        <p class="gateway-state">Enter total property count for this subscription.</p>
        <label class="downgrade-input-wrap">
          <span class="downgrade-input-label">Property count</span>
          <input
            v-model.number="pendingPropertyCount"
            class="downgrade-input"
            type="number"
            min="1"
            step="1"
            placeholder="e.g. 3"
          />
        </label>
        <p v-if="propertyCountError" class="gateway-state gateway-error">{{ propertyCountError }}</p>
        <div class="gateway-actions">
          <button type="button" class="btn-secondary" @click="closePropertyCountPrompt">Cancel</button>
          <button type="button" class="btn-primary" @click="confirmPropertyCount">Continue</button>
        </div>
      </div>
    </div>

    <div v-if="showSlotCountPrompt" class="gateway-overlay" @click.self="closeSlotCountPrompt">
      <div class="gateway-card" role="dialog" aria-modal="true" aria-labelledby="slot-count-title">
        <h3 id="slot-count-title">How many slots?</h3>
        <p class="gateway-state">Enter slot count for additional purchase.</p>
        <label class="downgrade-input-wrap">
          <span class="downgrade-input-label">Slot count</span>
          <input
            v-model.number="pendingSlotCount"
            class="downgrade-input"
            type="number"
            min="1"
            step="1"
            placeholder="e.g. 2"
          />
        </label>
        <p v-if="slotCountError" class="gateway-state gateway-error">{{ slotCountError }}</p>
        <div class="gateway-actions">
          <button type="button" class="btn-secondary" @click="closeSlotCountPrompt">Cancel</button>
          <button type="button" class="btn-primary" @click="confirmSlotCount">Continue</button>
        </div>
      </div>
    </div>

    <div
      v-if="showRemoveConfirm"
      class="gateway-overlay"
      @click.self="closeRemoveConfirm"
    >
      <div class="gateway-card remove-confirm-card" role="dialog" aria-modal="true" aria-labelledby="remove-confirm-title">
        <h3 id="remove-confirm-title">Remove property</h3>
        <p class="remove-confirm-message">
          Are you sure to remove this property? This can&apos;t be undo.
        </p>
        <p v-if="pendingRemoveRow" class="remove-confirm-target">
          {{ displayPropertyLabel(pendingRemoveRow) }}
        </p>
        <div class="gateway-actions">
          <button
            type="button"
            class="btn-secondary"
            :disabled="Boolean(removingObjectId)"
            @click="closeRemoveConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn-remove-property"
            :disabled="Boolean(removingObjectId)"
            @click="confirmRemoveSubscriptionObject"
          >
            {{ removingObjectId ? 'Removing...' : 'Yes, remove' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDowngradeConfirm"
      class="gateway-overlay"
      @click.self="closeDowngradeConfirm"
    >
      <div class="gateway-card remove-confirm-card" role="dialog" aria-modal="true" aria-labelledby="downgrade-confirm-title">
        <h3 id="downgrade-confirm-title">Confirm downgrade</h3>
        <p class="remove-confirm-message">
          Are you sure you want to downgrade? There is no refund.
        </p>
        <p class="remove-confirm-target">
          Target purchased slots: {{ targetPurchasedSlots }}
        </p>
        <div class="gateway-actions">
          <button
            type="button"
            class="btn-secondary"
            :disabled="isDowngrading"
            @click="closeDowngradeConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn-remove-property"
            :disabled="isDowngrading"
            @click="confirmDowngradeSlots"
          >
            {{ isDowngrading ? 'Downgrading...' : 'Yes, downgrade' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';
import { startEsewaSubscriptionCheckout, startEsewaAddSlotCheckout } from '@/utilities/esewaPayment';
import esewaLogo from '@/assets/esewa.png';
import khaltiLogo from '@/assets/khalti.png';

const SUBSCRIPTION_STORAGE_KEY = 'SUBSCRIPTION_DATA';
const GATEWAY_LOGO_MAP = {
  ESEWA: esewaLogo,
  KHALTI: khaltiLogo,
};

export default {
  name: 'ManageSubscription',
  props: {
    adminProfileData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      propertyActionMessage: '',
      propertyActionMessageTimer: null,
      removingObjectId: '',
      isDowngrading: false,
      targetPurchasedSlots: null,
      showDowngradeEditor: false,
      raw: null,
      plans: [],
      showPlans: false,
      showGatewayPicker: false,
      showPropertyCountPrompt: false,
      showSlotCountPrompt: false,
      gatewayLoading: false,
      gatewayError: '',
      gateways: [],
      pendingPlan: null,
      pendingPropertyCount: 1,
      propertyCountError: '',
      pendingSlotCount: 1,
      slotCountError: '',
      checkoutPurpose: 'SUBSCRIPTION',
      isOpeningGateway: false,
      showRemoveConfirm: false,
      pendingRemoveRow: null,
      showDowngradeConfirm: false,
      subscribeUrl:
        typeof process !== 'undefined' && process.env?.VUE_APP_SUBSCRIBE_URL
          ? String(process.env.VUE_APP_SUBSCRIBE_URL).trim()
          : '',
      includedFeatures: [
        'Dashboard',
        'Rooms',
        'Guests',
        'New Bookings',
        'Rent Payment',
        'Maintenance',
        'Outing Attendance',
        'Media Uploads',
        'Food and Menu',
        'Staff Management',
      ],
    };
  },
  computed: {
    isSubUser() {
      if (this.adminProfileData?.userType) {
        return String(this.adminProfileData.userType).toUpperCase() === 'SUBUSER';
      }
      try {
        const stored = localStorage.getItem('ADMIN_PROFILE_DATA');
        const parsed = stored ? JSON.parse(stored) : null;
        return String(parsed?.userType || '').toUpperCase() === 'SUBUSER';
      } catch {
        return false;
      }
    },
    normalized() {
      const d = this.raw;
      if (!d || typeof d !== 'object') {
        return null;
      }
      return {
        planId: d.planId ?? d.planid,
        planName: d.planname ?? d.planName,
        description: d.description,
        amount: d.amount,
        currency: d.currency,
        planInterval: d.planinterval ?? d.planInterval,
        objectCapacity: d.objectcapacity ?? d.objectCapacity,
        purchasedSlots: d.purchasedslots ?? d.purchasedSlots ?? null,
        remainingSlots: d.remainingslots ?? d.remainingSlots ?? null,
        planStatus: d.planstatus ?? d.planStatus,
        trialPeriodDays: Number(d.trialperioddays ?? d.trialPeriodDays ?? 0),
        planStartDate: d.planstartdate ?? d.planStartDate,
        nextBilling: d.nextbilling ?? d.nextBilling,
      };
    },
    /** Per-property subscription rows from API (`subscriptionObjects`, may be []). */
    subscriptionObjectRows() {
      const d = this.raw;
      if (!d || typeof d !== 'object') {
        return [];
      }
      const arr = d.subscriptionObjects ?? d.subscriptionobjects;
      if (!Array.isArray(arr)) {
        return [];
      }
      return arr.map((o) => ({
        planId: o.planid ?? o.planId,
        objectId: o.objectid ?? o.objectId,
        status: String(o.status ?? '').toUpperCase(),
        subscribedAt: o.subscribedat ?? o.subscribedAt ?? null,
        removedAt: o.removedat ?? o.removedAt ?? null,
      }));
    },
    objectIdToDisplayName() {
      try {
        const cached = localStorage.getItem('ADMIN_OBJECT_LIST');
        if (!cached) {
          return {};
        }
        const parsed = JSON.parse(cached);
        const list = Array.isArray(parsed?.data) ? parsed.data : [];
        const map = {};
        for (const ob of list) {
          const id = ob?.objectId ?? ob?.objectid;
          if (!id) {
            continue;
          }
          const name = ob?.objectName ?? ob?.objectname;
          map[String(id)] = name ? String(name) : '';
        }
        return map;
      } catch {
        return {};
      }
    },
    activeSubscriptionObjectCount() {
      return this.subscriptionObjectRows.filter((row) => row.status === 'ACTIVE').length;
    },
    purchasedSlotsCurrent() {
      const purchased = Number(this.normalized?.purchasedSlots);
      return Number.isFinite(purchased) ? purchased : 0;
    },
    canShowDowngradeSection() {
      return this.purchasedSlotsCurrent >= 0;
    },
    canSubmitDowngrade() {
      if (this.isDowngrading) {
        return false;
      }
      if (!this.showDowngradeEditor) {
        return false;
      }
      const target = Number(this.targetPurchasedSlots);
      if (!Number.isInteger(target)) {
        return false;
      }
      return target > 0;
    },
    intervalLabel() {
      const i = (this.normalized?.planInterval || '').toString().toUpperCase();
      if (i === 'YEAR' || i === 'YEARLY') return 'per year';
      if (i === 'MONTH' || i === 'MONTHLY') return 'per month';
      if (i === 'DAY' || i === 'DAILY') return 'per day';
      return i ? i.toLowerCase().replace(/_/g, ' ') : '';
    },
    statusClass() {
      const s = (this.normalized?.planStatus || '').toString().toUpperCase();
      if (s === 'ACTIVE') return 'status-active';
      if (s === 'TRIAL' || s === 'TRIALING') return 'status-trial';
      return 'status-neutral';
    },
  },
  mounted() {
    if (this.isSubUser) {
      this.loading = false;
      return;
    }
    if (this.loadCachedSubscription()) {
      this.loading = false;
      return;
    }
    this.fetchSubscription();
  },
  beforeUnmount() {
    if (this.propertyActionMessageTimer) {
      clearTimeout(this.propertyActionMessageTimer);
      this.propertyActionMessageTimer = null;
    }
  },
  methods: {
    notifyGlobalToast(message, type = 'error') {
      if (!message || typeof window === 'undefined') {
        return;
      }
      window.dispatchEvent(
        new CustomEvent('nexthome:toast', {
          detail: { message: String(message), type },
        })
      );
    },
    loadCachedSubscription() {
      try {
        const cached = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
        if (!cached) {
          return false;
        }
        const parsed = JSON.parse(cached);
        if (!parsed || typeof parsed !== 'object') {
          return false;
        }
        this.raw = parsed;
        this.syncTargetPurchasedSlotsFromRaw();
        this.errorMessage = '';
        this.showPlans = false;
        return true;
      } catch (error) {
        console.warn('Failed to read cached subscription:', error);
        return false;
      }
    },
    saveCachedSubscription(data) {
      if (!data || typeof data !== 'object') {
        return;
      }
      try {
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to cache subscription:', error);
      }
    },
    syncTargetPurchasedSlotsFromRaw() {
      const purchased = Number(this.raw?.purchasedslots ?? this.raw?.purchasedSlots);
      if (Number.isFinite(purchased) && purchased > 0) {
        this.targetPurchasedSlots = Math.max(1, purchased - 1);
      } else {
        this.targetPurchasedSlots = 1;
      }
      this.showDowngradeEditor = false;
    },
    displayPropertyLabel(row) {
      if (!row?.objectId) {
        return '—';
      }
      const id = String(row.objectId);
      const name = this.objectIdToDisplayName[id];
      if (name) {
        return name;
      }
      return id;
    },
    displayPropertySecondary(row) {
      if (!row?.objectId) {
        return '';
      }
      const id = String(row.objectId);
      const name = this.objectIdToDisplayName[id];
      return name ? `ID: ${id}` : '';
    },
    subscriptionObjectRowClass(row) {
      if (row.removedAt) {
        return 'is-removed';
      }
      const s = row.status;
      if (s === 'ACTIVE') {
        return 'is-active';
      }
      return 'is-neutral';
    },
    canRemoveSubscriptionObject(row) {
      return Boolean(
        row?.objectId &&
          row.status === 'ACTIVE' &&
          !row.removedAt
      );
    },
    removeSubscriptionObject(row) {
      if (!this.canRemoveSubscriptionObject(row)) {
        return;
      }
      if (this.removingObjectId) {
        return;
      }
      const objectId = String(row.objectId || '').trim();
      if (!objectId) {
        return;
      }
      this.showRemoveConfirm = false;
      if (this.propertyActionMessageTimer) {
        clearTimeout(this.propertyActionMessageTimer);
        this.propertyActionMessageTimer = null;
      }
      this.removingObjectId = objectId;
      this.propertyActionMessage = '';
      apiService.removeSubscriptionProperty(objectId)
        .then(() => {
          this.propertyActionMessage = 'Property removed from subscription successfully.';
          this.pendingRemoveRow = null;
          return Promise.all([
            this.fetchSubscription(),
            apiService.getObjectList()
              .then((response) => {
                const list = response?.data?.data;
                localStorage.setItem(
                  'ADMIN_OBJECT_LIST',
                  JSON.stringify({
                    data: Array.isArray(list) ? list : [],
                    timestamp: Date.now(),
                  })
                );
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('nexthome:object-list-refreshed'));
                }
              })
              .catch((error) => {
                console.error('Failed to refresh object list after property removal:', error);
              }),
          ]);
        })
        .catch((error) => {
          const message = this.extractErrorMessage(error);
          this.propertyActionMessage = message;
          this.notifyGlobalToast(message, 'error');
        })
        .finally(() => {
          this.removingObjectId = '';
          this.propertyActionMessageTimer = setTimeout(() => {
            this.propertyActionMessage = '';
            this.propertyActionMessageTimer = null;
          }, 5000);
        });
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status).replace(/_/g, ' ');
    },
    mapInterval(interval) {
      const i = String(interval || '').toUpperCase();
      if (i === 'YEAR' || i === 'YEARLY') return 'per year';
      if (i === 'MONTH' || i === 'MONTHLY') return 'per month';
      if (i === 'DAY' || i === 'DAILY') return 'per day';
      return i ? i.toLowerCase().replace(/_/g, ' ') : '';
    },
    /** SMS amounts are returned in minor units (e.g. paisa); convert for display. */
    formatMoney(amount, currency = 'NPR') {
      if (amount == null || amount === '') return '—';
      const n = Number(amount);
      if (Number.isNaN(n)) return '—';
      const major = n / 100;
      try {
        return new Intl.NumberFormat('en-NP', {
          style: 'currency',
          currency: currency || 'NPR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(major);
      } catch {
        return `${currency || 'NPR'} ${major.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      }
    },
    formatDateTime(value) {
      if (!value) return '—';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    },
    formatDateShort(value) {
      if (!value) return '—';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
    },
    extractErrorMessage(error) {
      const data = error?.response?.data;
      if (data?.reason) return data.reason;
      if (data?.message) return data.message;
      if (error?.message) return error.message;
      return 'Could not load subscription.';
    },
    isNoActiveSubscriptionError(error) {
      const errorCode = Number(error?.response?.data?.errorcode);
      return errorCode === 3231;
    },
    async fetchAvailablePlans() {
      try {
        const response = await apiService.getVisiblePlans();
        const list = response?.data?.data;
        this.plans = Array.isArray(list) ? list : [];
      } catch (planError) {
        console.error('getVisiblePlans failed:', planError);
        this.plans = [];
      }
    },
    async openGatewayPicker(plan, propertyCount) {
      this.pendingPlan = plan;
      this.pendingPropertyCount = Number(propertyCount) || 1;
      this.showGatewayPicker = true;
      this.gatewayLoading = true;
      this.gatewayError = '';
      this.gateways = [];
      try {
        const response = await apiService.getPaymentGateways();
        const list = Array.isArray(response?.data) ? response.data : [];
        this.gateways = list.filter((g) => String(g?.gatewaystatus || '').toUpperCase() === 'ACTIVE');
        if (!this.gateways.length) {
          this.gatewayError = 'No active payment gateway available right now.';
        }
      } catch (error) {
        console.error('Failed to fetch payment gateways:', error);
        this.gatewayError = error?.response?.data?.reason || 'Failed to load payment gateways.';
      } finally {
        this.gatewayLoading = false;
      }
    },
    closeGatewayPicker() {
      if (this.isOpeningGateway) {
        return;
      }
      this.showGatewayPicker = false;
      this.gatewayLoading = false;
      this.gatewayError = '';
      this.gateways = [];
      this.pendingPlan = null;
      this.pendingPropertyCount = 1;
      this.propertyCountError = '';
      this.pendingSlotCount = 1;
      this.slotCountError = '';
      this.checkoutPurpose = 'SUBSCRIPTION';
    },
    promptPropertyCount(plan) {
      this.pendingPlan = plan;
      this.pendingPropertyCount = 1;
      this.propertyCountError = '';
      this.checkoutPurpose = 'SUBSCRIPTION';
      this.showPropertyCountPrompt = true;
    },
    closePropertyCountPrompt() {
      this.showPropertyCountPrompt = false;
      this.propertyCountError = '';
      this.pendingPropertyCount = 1;
      this.pendingPlan = null;
      this.checkoutPurpose = 'SUBSCRIPTION';
    },
    confirmPropertyCount() {
      const count = Number(this.pendingPropertyCount);
      if (!Number.isInteger(count) || count <= 0) {
        this.propertyCountError = 'Please enter a valid property count.';
        return;
      }
      const plan = this.pendingPlan;
      this.showPropertyCountPrompt = false;
      this.propertyCountError = '';
      this.openGatewayPicker(plan, count);
    },
    async promptSlotCountPurchase() {
      let availabilityset = Array.isArray(this.raw?.availabilityset) ? this.raw.availabilityset : [];
      let availabilityid = this.raw?.availabilityid ?? this.raw?.availabilityId ?? '';
      let planId = this.normalized?.planId || this.raw?.planid || this.raw?.planId || '';
      if (!planId) {
        this.notifyGlobalToast('Unable to find active plan for slot purchase.', 'error');
        return;
      }
      if ((!availabilityset.length && !availabilityid) || !planId) {
        try {
          const response = await apiService.getSubscription();
          const latest = response?.data && typeof response.data === 'object' ? response.data : null;
          if (latest) {
            this.raw = latest;
            this.saveCachedSubscription(latest);
            this.syncTargetPurchasedSlotsFromRaw();
            availabilityset = Array.isArray(latest?.availabilityset) ? latest.availabilityset : [];
            availabilityid = latest?.availabilityid ?? latest?.availabilityId ?? '';
            planId = latest?.planId ?? latest?.planid ?? planId;
          }
        } catch (error) {
          console.error('Failed to refresh subscription before slot purchase:', error);
        }
      }
      this.pendingPlan = {
        planId,
        currency: this.normalized?.currency || this.raw?.currency || 'NPR',
        availabilityset,
        availabilityid,
      };
      if (
        !Array.isArray(this.pendingPlan.availabilityset) || !this.pendingPlan.availabilityset.length
      ) {
        if (!this.pendingPlan.availabilityid) {
          this.notifyGlobalToast('Plan availability is missing. Please refresh and try again.', 'error');
          return;
        }
      }
      this.pendingSlotCount = 1;
      this.slotCountError = '';
      this.checkoutPurpose = 'ADDSLOT';
      this.showSlotCountPrompt = true;
    },
    closeSlotCountPrompt() {
      this.showSlotCountPrompt = false;
      this.slotCountError = '';
      this.pendingSlotCount = 1;
      this.pendingPlan = null;
      this.checkoutPurpose = 'SUBSCRIPTION';
    },
    confirmSlotCount() {
      const count = Number(this.pendingSlotCount);
      if (!Number.isInteger(count) || count <= 0) {
        this.slotCountError = 'Please enter a valid slot count.';
        return;
      }
      const plan = this.pendingPlan;
      this.showSlotCountPrompt = false;
      this.slotCountError = '';
      this.openGatewayPicker(plan, 1);
    },
    openRemoveConfirm(row) {
      if (!this.canRemoveSubscriptionObject(row) || this.removingObjectId) {
        return;
      }
      this.pendingRemoveRow = row;
      this.showRemoveConfirm = true;
    },
    closeRemoveConfirm() {
      if (this.removingObjectId) {
        return;
      }
      this.showRemoveConfirm = false;
      this.pendingRemoveRow = null;
    },
    confirmRemoveSubscriptionObject() {
      if (!this.pendingRemoveRow) {
        return;
      }
      this.removeSubscriptionObject(this.pendingRemoveRow);
    },
    openDowngradeConfirm() {
      if (!this.canSubmitDowngrade) {
        return;
      }
      this.showDowngradeConfirm = true;
    },
    toggleDowngradeEditor() {
      if (this.isDowngrading) {
        return;
      }
      this.showDowngradeEditor = !this.showDowngradeEditor;
      if (this.showDowngradeEditor) {
        const purchased = this.purchasedSlotsCurrent;
        this.targetPurchasedSlots = purchased > 0 ? Math.max(1, purchased - 1) : 1;
      }
    },
    closeDowngradeConfirm() {
      if (this.isDowngrading) {
        return;
      }
      this.showDowngradeConfirm = false;
    },
    async confirmDowngradeSlots() {
      if (!this.canSubmitDowngrade) {
        return;
      }
      const target = Number(this.targetPurchasedSlots);
      this.isDowngrading = true;
      this.showDowngradeConfirm = false;
      if (this.propertyActionMessageTimer) {
        clearTimeout(this.propertyActionMessageTimer);
        this.propertyActionMessageTimer = null;
      }
      this.propertyActionMessage = '';
      try {
        await apiService.downgradeSubscriptionSlots(target);
        this.propertyActionMessage = 'Subscription slots downgraded successfully.';
        this.showDowngradeEditor = false;
        await Promise.all([
          this.fetchSubscription(),
          apiService.getObjectList()
            .then((response) => {
              const list = response?.data?.data;
              localStorage.setItem(
                'ADMIN_OBJECT_LIST',
                JSON.stringify({
                  data: Array.isArray(list) ? list : [],
                  timestamp: Date.now(),
                })
              );
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('nexthome:object-list-refreshed'));
              }
            })
            .catch((error) => {
              console.error('Failed to refresh object list after slot downgrade:', error);
            }),
        ]);
      } catch (error) {
        const message = this.extractErrorMessage(error);
        this.propertyActionMessage = message;
        this.notifyGlobalToast(message, 'error');
      } finally {
        this.isDowngrading = false;
        this.propertyActionMessageTimer = setTimeout(() => {
          this.propertyActionMessage = '';
          this.propertyActionMessageTimer = null;
        }, 5000);
      }
    },
    selectGateway(gateway) {
      const gatewayCode = String(gateway?.idgatewaycfg || '').toUpperCase();
      if (gatewayCode !== 'ESEWA') {
        this.gatewayError = `${gatewayCode || 'This'} gateway is coming soon.`;
        return;
      }
      this.isOpeningGateway = true;
      this.errorMessage = '';
      const checkoutPromise = this.checkoutPurpose === 'ADDSLOT'
        ? startEsewaAddSlotCheckout(this.pendingPlan, this.pendingSlotCount)
        : startEsewaSubscriptionCheckout(this.pendingPlan, this.pendingPropertyCount);
      checkoutPromise.catch((error) => {
        console.error('Failed to initiate eSewa payment:', error);
        this.gatewayError = error?.response?.data?.reason || error?.message || 'Could not start payment.';
        this.isOpeningGateway = false;
      });
    },
    gatewayLogo(code) {
      const key = String(code || '').toUpperCase();
      return GATEWAY_LOGO_MAP[key] || '';
    },
    subscribeToPlan(plan) {
      this.errorMessage = '';
      this.promptPropertyCount(plan);
    },
    async fetchSubscription() {
      this.loading = true;
      this.errorMessage = '';
      this.showPlans = false;
      this.plans = [];
      try {
        const { data } = await apiService.getSubscription();
        this.raw = data && typeof data === 'object' ? data : null;
        if (!this.raw) {
          this.errorMessage = 'No subscription data returned.';
        } else {
          this.syncTargetPurchasedSlotsFromRaw();
          this.saveCachedSubscription(this.raw);
        }
      } catch (error) {
        if (this.isNoActiveSubscriptionError(error)) {
          this.raw = null;
          this.errorMessage = '';
          this.showPlans = true;
          await this.fetchAvailablePlans();
          return;
        }
        if (!isNoDataError(error) && !isHmsSubscriptionInactiveError(error)) {
          console.error('getSubscription failed:', error);
        }
        this.errorMessage = this.extractErrorMessage(error);
        this.raw = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 16px 32px;
  z-index: 9999;
  overflow-y: auto;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow:
    0 24px 60px -18px rgba(31, 41, 55, 0.35),
    0 2px 8px rgba(91, 46, 142, 0.12);
  animation: slideUp 0.3s ease-out;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
}

.modal-content-compact {
  max-width: 720px;
}

.modal-content-plans {
  max-width: 980px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  padding: 24px 32px;
  position: relative;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 48px;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.modal-header p {
  margin: 0;
  font-size: 14px;
  opacity: 0.92;
  font-family: 'Poppins', sans-serif;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.body-wrap {
  padding: 20px 22px 22px;
  overflow-y: auto;
  max-height: none;
  min-height: 0;
  font-family: 'Poppins', sans-serif;
  background: linear-gradient(180deg, #ffffff 0%, #fafafd 100%);
  text-align: left;
  display: flex;
  flex-direction: column;
}

.state-block {
  text-align: left;
  padding: 1.2rem 0.6rem;
  color: #6b7280;
  font-size: 15px;
}

.loader-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 1rem 1rem 0.95rem;
  border-radius: 12px;
  background: linear-gradient(150deg, #ffffff 0%, #f7f4ff 100%);
  border: 1px solid rgba(91, 46, 142, 0.14);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.08);
}

.loader-card p {
  margin: 0;
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 600;
}

.loader-card small {
  margin: 0;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.4;
}

.loader-card-compact {
  padding: 0.85rem 0.9rem;
}

.state-error {
  color: #374151;
}

.state-error p {
  margin: 0 0 1rem;
}

.subuser-message-card {
  padding: 20px 18px;
  border-radius: 14px;
  border: 1px solid rgba(245, 158, 11, 0.28);
  background: linear-gradient(165deg, #fffbeb 0%, #ffffff 100%);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.12);
  text-align: left;
}

.subuser-message-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #b45309;
  background: rgba(245, 158, 11, 0.16);
  margin-bottom: 10px;
}

.subuser-message-card h3 {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #92400e;
}

.subuser-message-card p {
  margin: 0 0 14px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #78350f;
}

.plans-header {
  text-align: left;
  margin-bottom: 1.1rem;
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(120deg, rgba(91, 46, 142, 0.08), rgba(124, 58, 237, 0.04));
  border: 1px solid rgba(91, 46, 142, 0.12);
}

.plans-title {
  margin: 0 0 6px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
}

.plans-subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.55;
}

.feature-pill-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
}

.feature-pill {
  font-size: 11px;
  font-weight: 600;
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.08);
  border: 1px solid rgba(91, 46, 142, 0.15);
  border-radius: 999px;
  padding: 5px 10px;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.plans-grid-single {
  grid-template-columns: minmax(320px, 560px);
  justify-content: center;
}

.plan-card {
  border: 1px solid rgba(91, 46, 142, 0.16);
  border-radius: 16px;
  background: linear-gradient(170deg, #ffffff 0%, #faf8ff 55%, #f8f4ff 100%);
  padding: 18px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 13px;
  box-shadow: 0 10px 28px rgba(91, 46, 142, 0.1);
  min-height: 100%;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.plan-card:hover {
  transform: translateY(-2px);
  border-color: rgba(91, 46, 142, 0.28);
  box-shadow: 0 14px 34px rgba(91, 46, 142, 0.14);
}

.plan-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.plan-card-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.plan-card-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  min-height: 34px;
}

.plan-card-status {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 4px 8px;
}

.plan-card-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.plan-card-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5b2e8e;
}

.plan-card-interval {
  color: #6b7280;
  font-size: 12px;
}

.plan-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 30px;
}

.meta-chip {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 4px 8px;
}

.plan-feature-wrap {
  border-top: 1px solid rgba(91, 46, 142, 0.1);
  padding-top: 12px;
}

.subscribe-btn {
  width: 100%;
  margin-top: auto;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0;
  border-radius: 50%;
  border: 3px solid rgba(91, 46, 142, 0.16);
  border-top-color: #5b2e8e;
  border-right-color: #7c3aed;
  animation: spin 0.85s linear infinite;
}

.spinner-sm {
  width: 28px;
  height: 28px;
  border-width: 2.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.vh {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.status-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  flex-shrink: 0;
}

.status-active {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}

.status-trial {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 12px;
  word-break: break-all;
}

/* Active subscription: compact layout + scrollable property list */
.subscribed-layout {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.summary-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px 20px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(145deg, #faf8ff 0%, #ffffff 55%, #f5f0ff 100%);
  border: 1px solid rgba(91, 46, 142, 0.14);
  box-shadow: 0 4px 18px rgba(91, 46, 142, 0.07);
}

.summary-strip-text {
  min-width: 0;
  flex: 1;
}

.summary-title-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin-bottom: 4px;
}

.summary-plan-name {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.summary-plan-desc {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
}

.summary-strip-price {
  text-align: right;
  flex-shrink: 0;
}

.summary-price {
  display: block;
  font-size: 1.35rem;
  font-weight: 700;
  color: #5b2e8e;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.summary-interval {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.stat-chip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px;
}

.stat-chip {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(91, 46, 142, 0.1);
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.stat-chip-highlight {
  border-color: rgba(91, 46, 142, 0.22);
  background: linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%);
}

.stat-chip-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 3px;
}

.stat-chip-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  word-break: break-word;
}

.downgrade-panel {
  border-radius: 14px;
  border: 1px solid rgba(91, 46, 142, 0.14);
  background: linear-gradient(145deg, #faf8ff 0%, #ffffff 55%, #f5f0ff 100%);
  padding: 12px;
  box-shadow: 0 4px 18px rgba(91, 46, 142, 0.07);
}

.downgrade-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}

.downgrade-panel-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2937;
}

.downgrade-panel-meta {
  font-size: 10px;
  font-weight: 700;
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.12);
  border-radius: 999px;
  padding: 3px 9px;
}

.downgrade-panel-hint {
  margin: 0 0 10px;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
}

.downgrade-panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.downgrade-panel-form {
  margin-top: 10px;
  display: grid;
  grid-template-columns: minmax(0, 220px) auto;
  gap: 8px;
  align-items: end;
  justify-content: space-between;
}

.downgrade-input-wrap {
  min-width: 0;
  width: 100%;
  max-width: 220px;
}

.downgrade-input-label {
  display: block;
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.downgrade-input {
  width: 100%;
  height: 38px;
  border-radius: 9px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  padding: 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  background: #fff;
}

.downgrade-input:focus {
  outline: none;
  border-color: rgba(220, 38, 38, 0.45);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.14);
}

.downgrade-toggle-btn,
.downgrade-submit-btn {
  white-space: nowrap;
  height: 38px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 12px;
  align-self: end;
}

.downgrade-toggle-btn {
  background: #fff1f2;
  border: 1px solid rgba(220, 38, 38, 0.28);
  color: #b91c1c;
}

.downgrade-toggle-btn:hover {
  background: #ffe4e6;
}

.downgrade-submit-btn {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.24);
}

.properties-panel {
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.12);
  background: #fff;
  padding: 12px 12px 10px;
  box-shadow: 0 2px 10px rgba(91, 46, 142, 0.05);
  display: flex;
  flex-direction: column;
}

.properties-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}

.properties-panel-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.01em;
}

.properties-count-badge {
  font-size: 11px;
  font-weight: 700;
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.1);
  border-radius: 999px;
  padding: 3px 10px;
  min-width: 1.5rem;
  text-align: center;
}

.properties-panel-hint {
  margin: 0 0 10px;
  font-size: 11px;
  line-height: 1.45;
  color: #6b7280;
}

.properties-empty {
  margin: 0;
  padding: 10px 4px 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
}

.properties-table-scroller {
  max-height: min(280px, 42vh);
  overflow: auto;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #fafafa;
  -webkit-overflow-scrolling: touch;
}

.properties-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}

.properties-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  background: #f1f5f9;
  border-bottom: 1px solid rgba(148, 163, 184, 0.35);
  padding: 8px 10px;
  white-space: nowrap;
}

.properties-table tbody td {
  padding: 10px 10px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
}

.properties-table tbody tr:last-child td {
  border-bottom: none;
}

.properties-table tbody tr.is-removed td {
  opacity: 0.85;
  background: rgba(248, 250, 252, 0.9);
}

.col-property {
  min-width: 0;
  width: 38%;
}

.col-status {
  width: 22%;
}

.col-since {
  width: 26%;
}

.col-action {
  width: 14%;
  text-align: right;
  white-space: nowrap;
}

.cell-name {
  display: block;
  font-weight: 600;
  color: #111827;
  line-height: 1.35;
  word-break: break-word;
}

.cell-id {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.3;
}

.cell-date {
  display: block;
  color: #374151;
  font-weight: 500;
}

.cell-removed {
  display: block;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 600;
  color: #b45309;
}

.cell-dash {
  color: #cbd5e1;
  font-size: 14px;
}

.sub-object-status {
  display: inline-block;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 9px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
}

.sub-status-active {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}

.sub-status-cancelled {
  background: rgba(245, 158, 11, 0.18);
  color: #b45309;
}

.sub-status-removed {
  background: rgba(148, 163, 184, 0.28);
  color: #475569;
}

.btn-remove-property {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgba(220, 38, 38, 0.45);
  background: #fff;
  color: #b91c1c;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.btn-remove-property:hover {
  background: rgba(254, 226, 226, 0.6);
  border-color: rgba(185, 28, 28, 0.55);
}

.btn-remove-property:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8fafc;
}

.remove-confirm-card {
  max-width: 460px;
}

.remove-confirm-message {
  margin: 0;
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.5;
}

.remove-confirm-target {
  margin: 10px 0 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
}

.property-action-hint {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #1e40af;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid rgba(91, 46, 142, 0.12);
  flex-wrap: wrap;
}

.footer-actions--subscribed {
  margin-top: 0;
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 100%;
  }

  .body-wrap {
    padding: 16px 14px 18px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .summary-strip-price {
    text-align: left;
    width: 100%;
  }

  .stat-chip-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .downgrade-panel-form {
    grid-template-columns: 1fr;
  }

  .downgrade-toggle-btn,
  .downgrade-submit-btn,
  .downgrade-input-wrap {
    width: 100%;
    max-width: 100%;
  }

  .properties-table-scroller {
    max-height: min(320px, 48vh);
  }

  .col-property {
    width: auto;
  }

  .col-status,
  .col-since,
  .col-action {
    width: auto;
  }
}

.btn-secondary,
.btn-primary {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.25);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(91, 46, 142, 0.3);
}

/* Explicit red styling for downgrade actions (overrides shared button theme). */
.btn-secondary.downgrade-toggle-btn {
  background: #fff1f2;
  border: 1px solid rgba(220, 38, 38, 0.28);
  color: #b91c1c;
}

.btn-secondary.downgrade-toggle-btn:hover {
  background: #ffe4e6;
}

.btn-primary.downgrade-submit-btn {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #fff;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.24);
}

.btn-primary.downgrade-submit-btn:hover {
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.3);
}

.gateway-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10020;
  padding: 16px;
}

.gateway-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.24);
  text-align: left;
}

.gateway-card h3 {
  margin: 0 0 10px;
  font-size: 1.05rem;
  color: #1f2937;
}

.gateway-state {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.gateway-error {
  color: #b91c1c;
}

.gateway-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.gateway-btn {
  width: 100%;
  border: 1px solid rgba(91, 46, 142, 0.18);
  background: #fff;
  border-radius: 12px;
  padding: 11px 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  color: #1f2937;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}

.gateway-btn:hover {
  border-color: rgba(91, 46, 142, 0.34);
  box-shadow: 0 6px 15px rgba(91, 46, 142, 0.12);
  transform: translateY(-1px);
}

.gateway-btn-main {
  display: inline-flex;
  align-items: center;
  gap: 0.9rem;
}

.gateway-logo-wrap {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.gateway-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.gateway-meta {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.gateway-meta strong {
  font-size: 0.98rem;
  font-weight: 700;
  color: #111827;
}

.gateway-btn small {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.gateway-chevron {
  color: #64748b;
  font-size: 1.25rem;
  font-weight: 700;
}

.gateway-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
</style>
